import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import { UserFormData, DeactivationRequest } from '../../types/user';
import { useNavigate, Link } from 'react-router-dom';
import { CameraIcon, ShieldCheckIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import ResidencePicker from '../../components/common/ResidencePicker';

const ProfileSettingsPage: React.FC = () => {
  const { user, token, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileData, setProfileData] = useState<UserFormData>({} as UserFormData);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivationReason, setDeactivationReason] = useState('');
  const [deactivating, setDeactivating] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [acceptingPrivacyPolicy, setAcceptingPrivacyPolicy] = useState(false);
  const [acceptingApplicationConsent, setAcceptingApplicationConsent] = useState(false);
  const [acceptingConnectConsent, setAcceptingConnectConsent] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        console.log('Fetching user profile data');
        
        const response = await fetch(`/api/auth/me`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log('Profile data received:', data);
          console.log('Profile picture URL from backend:', data.profilePictureUrl);
          setProfileData(data);
          
          // If there's a profile picture URL, pre-fetch it to check availability
          if (data.profilePictureUrl) {
            console.log('Attempting to pre-fetch profile picture:', data.profilePictureUrl);
            const img = new Image();
            img.onload = () => console.log('Profile picture pre-fetch successful');
            img.onerror = (e) => console.error('Profile picture pre-fetch failed:', e);
            img.src = data.profilePictureUrl;
          }
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
        toast.error('Failed to load profile data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchUserProfile();
    } else {
      setLoading(false);
    }
  }, [token]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setProfileData({
      ...profileData,
      [name]: value
    });
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    
    const file = e.target.files[0];
    
    // Validate file size (500KB limit)
    const maxSize = 500 * 1024; // 500KB
    if (file.size > maxSize) {
      const fileSizeKB = (file.size / 1024).toFixed(2);
      toast.error(`Profile picture is too large. Maximum size is 500KB, but your file is ${fileSizeKB}KB. Please compress or reduce the file size and try again.`);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    setUploadingImage(true);
    setImageLoading(true);  // Reset image loading state
    setImageError(false);   // Clear any previous errors
    
    try {
      // Step 1: Upload the image file
      console.log('Uploading profile picture...');
      const response = await fetch('/api/files/upload/profile-picture', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Profile picture upload response:', data);
        
        // Step 2: Update the profile data with the new profile picture URL
        const imageUrl = data.url;
        console.log('Setting profile picture URL to:', imageUrl);
        
        const updatedProfileData = {
          ...profileData,
          profilePictureUrl: imageUrl,
        };
        
        setProfileData(updatedProfileData);
        
        // Step 3: Update the user profile via correct endpoint for self-updates
        console.log('Saving updated profile with new picture URL to backend...');
        const updateResponse = await fetch(`/api/auth/me`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(updatedProfileData)
        });
        
        if (updateResponse.ok) {
          // Step 4: Update the user in the auth context
          if (user) {
            console.log('Updating user in AuthContext with profile picture URL:', imageUrl);
            setUser({
              ...user,
              profilePictureUrl: imageUrl
            });
          }
          
          toast.success('Profile picture uploaded and saved successfully');
        } else {
          console.error('Failed to update profile with new picture, status:', updateResponse.status);
          const errorData = await updateResponse.json();
          console.error('Error response:', errorData);
          toast.warning('Profile picture uploaded but not saved to profile');
        }
      } else {
        // Handle 413 Payload Too Large error
        if (response.status === 413) {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage = errorData.message || 'Profile picture is too large. Maximum size is 500KB. Please compress or reduce the file size and try again.';
          toast.error(errorMessage);
        } else {
          const error = await response.json();
          console.error('Failed to upload profile picture:', error);
          toast.error(error.message || 'Failed to upload profile picture');
        }
      }
    } catch (error: any) {
      console.error('Error uploading profile picture:', error);
      // Handle 413 errors from network level
      if (error.response?.status === 413) {
        const errorMessage = error.response?.data?.message || 'Profile picture is too large. Maximum size is 500KB. Please compress or reduce the file size and try again.';
        toast.error(errorMessage);
      } else {
        toast.error('Network error when uploading profile picture');
      }
    } finally {
      setUploadingImage(false);
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      console.log('Updating profile with data:', profileData);
      const response = await fetch(`/api/auth/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(profileData)
      });

      if (response.ok) {
        const updatedUserData = await response.json();
        console.log('Updated user data received from backend:', updatedUserData);
        
        // Update the user in the auth context with the response data
        if (setUser) {
          setUser(updatedUserData);
        }
        
        toast.success('Profile updated successfully!');
        navigate('/profile'); // Navigate back to profile page after successful update
      } else {
        const errorData = await response.json();
        console.error('Failed to update profile:', errorData);
        toast.error(errorData.message || 'Failed to update profile.');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleAcceptPrivacyPolicy = async () => {
    setAcceptingPrivacyPolicy(true);
    try {
      const response = await fetch(`/api/auth/accept-privacy-policy`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const updatedUserData = await response.json();
        console.log('Privacy Policy accepted, updated user data:', updatedUserData);
        
        // Update the user in the auth context
        if (setUser) {
          setUser(updatedUserData);
        }
        
        // Update local profile data
        setProfileData(updatedUserData);
        
        toast.success('Privacy Policy accepted successfully!');
      } else {
        const errorData = await response.json();
        console.error('Failed to accept Privacy Policy:', errorData);
        toast.error(errorData.message || 'Failed to accept Privacy Policy.');
      }
    } catch (error) {
      console.error('Error accepting Privacy Policy:', error);
      toast.error('Network error. Please try again.');
    } finally {
      setAcceptingPrivacyPolicy(false);
    }
  };

  const handleAcceptApplicationConsent = async () => {
    setAcceptingApplicationConsent(true);
    try {
      const response = await fetch(`/api/auth/accept-application-consent`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const updatedUserData = await response.json();
        console.log('Application Consent accepted, updated user data:', updatedUserData);
        
        // Update the user in the auth context
        if (setUser) {
          setUser(updatedUserData);
        }
        
        // Update local profile data
        setProfileData(updatedUserData);
        
        toast.success('Application consent accepted successfully!');
      } else {
        const errorData = await response.json();
        console.error('Failed to accept Application Consent:', errorData);
        toast.error(errorData.message || 'Failed to accept application consent.');
      }
    } catch (error) {
      console.error('Error accepting Application Consent:', error);
      toast.error('Network error. Please try again.');
    } finally {
      setAcceptingApplicationConsent(false);
    }
  };

  const handleAcceptConnectConsent = async () => {
    setAcceptingConnectConsent(true);
    try {
      const response = await fetch(`/api/auth/accept-connect-consent`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const updatedUserData = await response.json();
        console.log('Connect Consent accepted, updated user data:', updatedUserData);
        
        // Update the user in the auth context
        if (setUser) {
          setUser(updatedUserData);
        }
        
        // Update local profile data
        setProfileData(updatedUserData);
        
        toast.success('Connect consent accepted successfully!');
      } else {
        const errorData = await response.json();
        console.error('Failed to accept Connect Consent:', errorData);
        toast.error(errorData.message || 'Failed to accept Connect consent.');
      }
    } catch (error) {
      console.error('Error accepting Connect Consent:', error);
      toast.error('Network error. Please try again.');
    } finally {
      setAcceptingConnectConsent(false);
    }
  };

  const handleDeactivateAccount = async () => {
    if (!deactivationReason.trim()) {
      toast.error('Please provide a reason for deactivating your account.');
      return;
    }

    setDeactivating(true);
    try {
      const request: DeactivationRequest = { reason: deactivationReason };
      // Use the auth/deactivate endpoint for deactivating own account
      const response = await fetch(`/api/auth/deactivate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(request)
      });

      if (response.ok) {
        toast.success('Your account has been deactivated.');
        // Logout the user after deactivation
        setTimeout(() => {
          logout();
          navigate('/login');
        }, 1500);
      } else {
        const errorData = await response.json();
        console.error('Error deactivating account:', errorData);
        toast.error(errorData.message || 'Failed to deactivate account.');
      }
    } catch (error) {
      console.error('Error deactivating account:', error);
      toast.error('Network error. Please try again.');
    } finally {
      setDeactivating(false);
      setShowDeactivateModal(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 dark:border-indigo-400"></div>
      </div>
    );
  }

  // Edit mode content (now the main content)
  return (
    <>
      <div className="bg-white dark:bg-gray-800 shadow-lg dark:shadow-[0_10px_15px_-3px_rgba(0,0,0,0.3),0_4px_6px_-2px_rgba(0,0,0,0.2)] sm:rounded-lg border border-gray-200/50 dark:border-gray-700/50">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">Profile Settings</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">Update your personal information.</p>
          </div>
          <button
            onClick={() => navigate('/profile')}
            className="inline-flex justify-center py-2 px-4 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 transition-colors"
          >
            Cancel
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6 px-4 py-5 sm:p-6">
          {/* Profile Picture with File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Profile Photo</label>
            <div className="mt-2 flex justify-center">
              <div className="relative">
                {profileData.profilePictureUrl ? (
                  <>
                    <div className="relative h-32 w-32">
                      {imageLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-700 rounded-full border-4 border-white dark:border-gray-700 shadow">
                          <div className="animate-spin h-8 w-8 border-b-2 border-indigo-500 dark:border-indigo-400"></div>
                        </div>
                      )}
                      <img 
                        src={profileData.profilePictureUrl} 
                        alt={`${profileData.firstName} ${profileData.lastName}`}
                        className={`h-32 w-32 rounded-full object-cover border-4 border-white dark:border-gray-700 shadow ${imageError ? 'hidden' : ''}`}
                        onLoad={() => {
                          console.log('Profile image loaded successfully');
                          setImageLoading(false);
                          setImageError(false);
                        }}
                        onError={(e) => {
                          console.error('Error loading profile image:', profileData.profilePictureUrl);
                          setImageLoading(false);
                          setImageError(true);
                          const target = e.target as HTMLImageElement;
                          target.onerror = null; // Prevent infinite fallback loop
                        }}
                      />
                      {imageError && (
                        <div className="absolute inset-0 flex items-center justify-center bg-red-100 dark:bg-red-900/30 rounded-full border-4 border-white dark:border-gray-700 shadow">
                          <span className="text-red-500 dark:text-red-400 text-sm text-center px-2">Image could not be loaded</span>
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center w-32 break-words">
                      {imageError && (
                        <p className="text-red-500 dark:text-red-400 mt-1">Error loading image. Please upload a new one.</p>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="h-32 w-32 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-800 dark:text-indigo-300 text-3xl font-medium border-4 border-white dark:border-gray-700 shadow">
                    {profileData.firstName?.[0]}{profileData.lastName?.[0]}
                  </div>
                )}
                <div 
                  className="absolute bottom-0 right-0 h-10 w-10 rounded-full bg-indigo-600 dark:bg-indigo-500 flex items-center justify-center cursor-pointer hover:bg-indigo-700 dark:hover:bg-indigo-600 transition-colors"
                  onClick={handleImageClick}
                >
                  {uploadingImage ? (
                    <div className="animate-spin h-6 w-6 border-2 border-white rounded-full border-t-transparent"></div>
                  ) : (
                    <CameraIcon className="h-6 w-6 text-white" />
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageUpload}
                />
              </div>
            </div>
          </div>

          {/* Basic Information */}
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div className="sm:col-span-3">
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                First name
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  name="firstName"
                  id="firstName"
                  value={profileData.firstName || ''}
                  onChange={handleChange}
                  required
                  className="block w-full py-3 px-4 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-transparent shadow-sm transition-all duration-200 hover:border-gray-400 dark:hover:border-gray-500"
                />
              </div>
            </div>

            <div className="sm:col-span-3">
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Last name
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  name="lastName"
                  id="lastName"
                  value={profileData.lastName || ''}
                  onChange={handleChange}
                  required
                  className="block w-full py-3 px-4 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-transparent shadow-sm transition-all duration-200 hover:border-gray-400 dark:hover:border-gray-500"
                />
              </div>
            </div>

            <div className="sm:col-span-6">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email address
              </label>
              <div className="mt-1">
                <input
                  type="email"
                  name="email"
                  id="email"
                  value={profileData.email || ''}
                  disabled
                  className="block w-full py-3 px-4 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-600 text-gray-900 dark:text-gray-100 cursor-not-allowed shadow-sm"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Email address cannot be changed.</p>
              </div>
            </div>

            <div className="sm:col-span-6">
              <label htmlFor="bio" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Bio
              </label>
              <div className="mt-1">
                <textarea
                  id="bio"
                  name="bio"
                  rows={3}
                  value={profileData.bio || ''}
                  onChange={handleChange}
                  className="block w-full py-3 px-4 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-transparent shadow-sm transition-all duration-200 hover:border-gray-400 dark:hover:border-gray-500"
                  placeholder="Brief description about yourself"
                />
              </div>
            </div>

            <div className="sm:col-span-3">
              <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Phone number
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  name="phoneNumber"
                  id="phoneNumber"
                  value={profileData.phoneNumber || ''}
                  onChange={handleChange}
                  className="block w-full py-3 px-4 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-transparent shadow-sm transition-all duration-200 hover:border-gray-400 dark:hover:border-gray-500"
                />
              </div>
            </div>

            <div className="sm:col-span-3">
              <label htmlFor="birthDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Birth date
              </label>
              <div className="mt-1 relative">
                <input
                  type="date"
                  name="birthDate"
                  id="birthDate"
                  value={profileData.birthDate || ''}
                  onChange={handleChange}
                  className="block w-full py-3 px-4 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-transparent shadow-sm transition-all duration-200 hover:border-gray-400 dark:hover:border-gray-500 [&::-webkit-calendar-picker-indicator]:dark:filter [&::-webkit-calendar-picker-indicator]:dark:invert [&::-webkit-calendar-picker-indicator]:dark:opacity-70 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                  style={{
                    colorScheme: 'light dark'
                  }}
                />
              </div>
            </div>

            <div className="sm:col-span-6">
              <label htmlFor="linkedinProfileUrl" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                LinkedIn Profile URL
              </label>
              <div className="mt-1">
                <input
                  type="url"
                  name="linkedinProfileUrl"
                  id="linkedinProfileUrl"
                  value={profileData.linkedinProfileUrl || ''}
                  onChange={handleChange}
                  className="block w-full py-3 px-4 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-transparent shadow-sm transition-all duration-200 hover:border-gray-400 dark:hover:border-gray-500"
                />
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className="pt-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100">Address Information</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Use a permanent address where you can receive mail.</p>
          </div>

          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div className="sm:col-span-6">
              <label htmlFor="addressLine1" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Street address
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  name="addressLine1"
                  id="addressLine1"
                  value={profileData.addressLine1 || ''}
                  onChange={handleChange}
                  className="block w-full py-3 px-4 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-transparent shadow-sm transition-all duration-200 hover:border-gray-400 dark:hover:border-gray-500"
                />
              </div>
            </div>

            <div className="sm:col-span-6">
              <label htmlFor="addressLine2" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Apartment, suite, etc.
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  name="addressLine2"
                  id="addressLine2"
                  value={profileData.addressLine2 || ''}
                  onChange={handleChange}
                  className="block w-full py-3 px-4 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-transparent shadow-sm transition-all duration-200 hover:border-gray-400 dark:hover:border-gray-500"
                />
              </div>
            </div>

            <div className="sm:col-span-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Place of Residence
              </label>
              <div className="mt-1">
                <ResidencePicker
                  city={profileData.city || ''}
                  country={profileData.country || ''}
                  onChange={({ city, country }) => setProfileData((prev) => ({ ...prev, city, country }))}
                />
              </div>
            </div>

            <div className="sm:col-span-3">
              <label htmlFor="state" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                State / Province
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  name="state"
                  id="state"
                  value={profileData.state || ''}
                  onChange={handleChange}
                  className="block w-full py-3 px-4 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-transparent shadow-sm transition-all duration-200 hover:border-gray-400 dark:hover:border-gray-500"
                />
              </div>
            </div>

            <div className="sm:col-span-3">
              <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                ZIP / Postal code
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  name="postalCode"
                  id="postalCode"
                  value={profileData.postalCode || ''}
                  onChange={handleChange}
                  className="block w-full py-3 px-4 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-transparent shadow-sm transition-all duration-200 hover:border-gray-400 dark:hover:border-gray-500"
                />
              </div>
            </div>

          </div>

          {/* Privacy Policy Section */}
          <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100 mb-4">Privacy Policy</h3>
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  {profileData.privacyPolicyAccepted ? (
                    <CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
                  ) : (
                    <ShieldCheckIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
                  )}
                </div>
                <div className="ml-3 flex-1">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    Privacy Policy Acceptance
                  </h4>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    {profileData.privacyPolicyAccepted ? (
                      <>
                        You have accepted the Privacy Policy. This is required to submit job applications.
                        {profileData.privacyPolicyAcceptedAt && (
                          <span className="block mt-1 text-xs text-gray-500 dark:text-gray-500">
                            Accepted on: {new Date(profileData.privacyPolicyAcceptedAt).toLocaleDateString()}
                          </span>
                        )}
                        <span className="block mt-2 text-xs text-gray-500 dark:text-gray-500">
                          If you need to withdraw your acceptance, please contact support through the chat feature.
                        </span>
                      </>
                    ) : (
                      <>
                        You must accept the Privacy Policy to submit job applications. Please read and accept our{' '}
                        <Link 
                          to="/privacy-policy" 
                          target="_blank"
                          className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300 underline"
                        >
                          Privacy Policy
                        </Link>
                        {' '}to continue.
                      </>
                    )}
                  </p>
                  {!profileData.privacyPolicyAccepted && (
                    <div className="mt-4">
                      <button
                        type="button"
                        onClick={handleAcceptPrivacyPolicy}
                        disabled={acceptingPrivacyPolicy}
                        className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 dark:from-indigo-500 dark:to-indigo-600 dark:hover:from-indigo-600 dark:hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] disabled:transform-none transition-all"
                      >
                        {acceptingPrivacyPolicy ? 'Accepting...' : 'Accept Privacy Policy'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Application Consent Section */}
          <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100 mb-4">Application Consent</h3>
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  {profileData.applicationConsentGiven ? (
                    <CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
                  ) : (
                    <ShieldCheckIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
                  )}
                </div>
                <div className="ml-3 flex-1">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    Application Consent Terms
                  </h4>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    {profileData.applicationConsentGiven ? (
                      <>
                        You have accepted the application consent terms. This is required to submit job applications.
                        {profileData.applicationConsentGivenAt && (
                          <span className="block mt-1 text-xs text-gray-500 dark:text-gray-500">
                            Accepted on: {new Date(profileData.applicationConsentGivenAt).toLocaleDateString()}
                          </span>
                        )}
                        <span className="block mt-2 text-xs text-gray-500 dark:text-gray-500">
                          If you need to withdraw your consent, please contact support through the chat feature.
                        </span>
                      </>
                    ) : (
                      <>
                        You must accept the application consent terms to submit job applications. By accepting, you confirm that you have read the{' '}
                        <Link 
                          to="/privacy-policy" 
                          target="_blank"
                          className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300 underline"
                        >
                          Privacy Policy
                        </Link>
                        {' '}and confirm that IST stores your personal details to be able to process your application.
                      </>
                    )}
                  </p>
                  {!profileData.applicationConsentGiven && (
                    <div className="mt-4">
                      <button
                        type="button"
                        onClick={handleAcceptApplicationConsent}
                        disabled={acceptingApplicationConsent}
                        className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 dark:from-indigo-500 dark:to-indigo-600 dark:hover:from-indigo-600 dark:hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] disabled:transform-none transition-all"
                      >
                        {acceptingApplicationConsent ? 'Accepting...' : 'Accept Application Consent'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Connect Consent Section */}
          <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100 mb-4">Connect Consent</h3>
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  {profileData.connectConsentGiven ? (
                    <CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
                  ) : (
                    <ShieldCheckIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
                  )}
                </div>
                <div className="ml-3 flex-1">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    Connect Consent Terms
                  </h4>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    {profileData.connectConsentGiven ? (
                      <>
                        You have accepted the Connect consent terms. This allows IST to store your personal details to contact you for future job opportunities.
                        {profileData.connectConsentGivenAt && (
                          <span className="block mt-1 text-xs text-gray-500 dark:text-gray-500">
                            Accepted on: {new Date(profileData.connectConsentGivenAt).toLocaleDateString()}
                          </span>
                        )}
                        <span className="block mt-2 text-xs text-gray-500 dark:text-gray-500">
                          If you need to withdraw your consent, please contact support through the chat feature.
                        </span>
                      </>
                    ) : (
                      <>
                        By accepting this consent, you confirm that you have read the{' '}
                        <Link 
                          to="/privacy-policy" 
                          target="_blank"
                          className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300 underline"
                        >
                          Privacy Policy
                        </Link>
                        {' '}and confirm that IST store your personal details to be able to contact you for future job opportunities.
                        <br /><br />
                        IST will hold your data for future employment opportunities for a maximum period of 2 years, or until you decide to withdraw your consent.
                      </>
                    )}
                  </p>
                  {!profileData.connectConsentGiven && (
                    <div className="mt-4">
                      <button
                        type="button"
                        onClick={handleAcceptConnectConsent}
                        disabled={acceptingConnectConsent}
                        className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 dark:from-indigo-500 dark:to-indigo-600 dark:hover:from-indigo-600 dark:hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] disabled:transform-none transition-all"
                      >
                        {acceptingConnectConsent ? 'Accepting...' : 'Accept Connect Consent'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Account Actions */}
          <div className="pt-6">
            {user?.role === 'ADMIN' ? (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="text-sm text-gray-500 dark:text-gray-400 italic flex-shrink-0">
                  Admin accounts cannot be self-deactivated. Please ask another admin to deactivate your account if needed.
                </div>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 dark:from-indigo-500 dark:to-indigo-600 dark:hover:from-indigo-600 dark:hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] disabled:transform-none transition-all w-full sm:w-auto"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row justify-between gap-4">
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 dark:from-indigo-500 dark:to-indigo-600 dark:hover:from-indigo-600 dark:hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] disabled:transform-none transition-all w-full sm:w-auto"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 dark:from-red-500 dark:to-red-600 dark:hover:from-red-600 dark:hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 dark:focus:ring-red-400 transform hover:scale-[1.02] transition-all w-full sm:w-auto"
                  onClick={() => setShowDeactivateModal(true)}
                >
                  Deactivate Account
                </button>
              </div>
            )}
          </div>
        </form>
      </div>

      {/* Deactivation Modal */}
      {showDeactivateModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 dark:bg-gray-900 opacity-75 dark:opacity-85"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl dark:shadow-[0_20px_25px_-5px_rgba(0,0,0,0.5),0_10px_10px_-5px_rgba(0,0,0,0.3)] transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6 border border-gray-200/50 dark:border-gray-700/50">
              <div>
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30">
                  <svg className="h-6 w-6 text-red-600 dark:text-red-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
                    Deactivate Account
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Are you sure you want to deactivate your account? This action cannot be undone. All of your data will be inaccessible until an administrator reactivates your account.
                    </p>
                    <div className="mt-4">
                      <label htmlFor="deactivationReason" className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left mb-2">
                        Please tell us why you're leaving:
                      </label>
                      <textarea
                        id="deactivationReason"
                        name="deactivationReason"
                        rows={3}
                        required
                        value={deactivationReason}
                        onChange={(e) => setDeactivationReason(e.target.value)}
                        className="block w-full py-3 px-4 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 focus:border-transparent shadow-sm transition-all duration-200 hover:border-gray-400 dark:hover:border-gray-500"
                        placeholder="Your feedback helps us improve our service"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  type="button"
                  disabled={deactivating}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 dark:from-red-500 dark:to-red-600 dark:hover:from-red-600 dark:hover:to-red-700 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 dark:focus:ring-red-400 sm:col-start-2 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] disabled:transform-none transition-all"
                  onClick={handleDeactivateAccount}
                >
                  {deactivating ? 'Deactivating...' : 'Deactivate'}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-700 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 sm:mt-0 sm:col-start-1 sm:text-sm transition-colors"
                  onClick={() => setShowDeactivateModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProfileSettingsPage; 